import { defineStore } from 'pinia'
import { ensureDeviceId } from '@/assets/js/device.js'
import { registerWithDeviceId, getUserInfo, getUserDetail, loginWithThirdParty } from '@/service/user'
import { syncRewardStorageForAccount, clearRewardBoundSub } from '@/service/rewardAccount'
import { clearRewardTasksCache } from '@/service/rewardService'
import { clearPlaybackRecords } from '@/service/playbackRecord'
import { usePlayBarStore } from '@/store/playBarStore'
import { refreshAnalyticsUserProperties } from '@/assets/js/firebase'

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  try {
    return JSON.parse(atob(padded))
  } catch (e) {
    return null
  }
}

/** Facebook/Cognito 可能返回纯 URL 或 { data: { url } } JSON 字符串 */
function normalizeAvatarUrl(raw) {
  if (raw == null || raw === '') return ''

  if (typeof raw === 'object') {
    const url = raw?.data?.url || raw?.url
    return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : ''
  }

  if (typeof raw !== 'string') return ''

  const trimmed = raw.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed)
      const url = parsed?.data?.url || parsed?.url
      return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : ''
    } catch {
      return ''
    }
  }

  return ''
}

function pickAvatarFromProfile(profile) {
  if (!profile || typeof profile !== 'object') return ''
  const candidates = [
    profile.picture,
    profile['custom:picture'],
    profile.avatar,
    profile.picture_url
  ]
  for (const item of candidates) {
    const url = normalizeAvatarUrl(item)
    if (url) return url
  }
  return ''
}

export const useUserStore = defineStore('user', {
  // state中定义数据
  state: () => ({
    deviceId: localStorage.getItem('deviceId') || '',
    uid: localStorage.getItem('uid') || '',
    userType: localStorage.getItem('userType') || '',
    userInfo: JSON.parse(localStorage.getItem('user_info')) || { watchCoins: 0, vip: false, uid: '' },
    cognitoTokens: JSON.parse(localStorage.getItem('cognito_tokens') || 'null'),
    cognitoProfile: JSON.parse(localStorage.getItem('cognito_profile') || 'null'),
    _sessionReady: null
  }),
  // 2. 计算属性
  getters: {
    isCognitoLoggedIn: (state) => {
      if (!state.cognitoTokens?.idToken) return false
      const expiresAt = Number(state.cognitoTokens?.expiresAt || 0)
      return expiresAt > Date.now()
    },
    isLoggedIn: (state) => {
      const backendLoggedIn = Number(state.userInfo?.loginFrom || 0) !== 0
      if (backendLoggedIn) return true
      if (!state.cognitoTokens?.idToken) return false
      const expiresAt = Number(state.cognitoTokens?.expiresAt || 0)
      return expiresAt > Date.now()
    },
    displayName: (state) => {
      const serverNickname = String(state.userInfo?.nickname || '').trim()
      if (serverNickname) {
        return serverNickname
      }

      const cognitoValid =
        !!state.cognitoTokens?.idToken &&
        Number(state.cognitoTokens?.expiresAt || 0) > Date.now()
      if (cognitoValid) {
        return (
          state.cognitoProfile?.name ||
          state.cognitoProfile?.email ||
          'Guest'
        )
      }

      return 'Guest'
    },
    displayAvatar: (state) => {
      return (
        normalizeAvatarUrl(state.cognitoProfile?.picture) ||
        normalizeAvatarUrl(state.userInfo?.avatar) ||
        ''
      )
    }
  },
  // 3. 修改数据的方法
  actions: {
    clearPlaybackState() {
      clearPlaybackRecords()
      usePlayBarStore().clear()
    },
    clearPlaybackStateIfAccountChanged(nextUid) {
      const prevUid = this.uid || localStorage.getItem('uid') || ''
      if (prevUid && nextUid && String(prevUid) !== String(nextUid)) {
        this.clearPlaybackState()
      }
    },
    async initUserSession() {
      if (!this._sessionReady) {
        this._sessionReady = this._doInitSession()
      }
      return this._sessionReady
    },
    async _doInitSession() {
      this.deviceId = await ensureDeviceId()
      logger.debug('initUserSession: ' + this.deviceId)
      let userData = null
      try {
        if (!this.uid) {
          userData = await registerWithDeviceId()
        } else {
          userData = await getUserInfo()
        }
      } catch (error) {
        logger.debug(error)
      }

      logger.debug('getUserInfo userData = ', userData)
      if (userData?.userInfo?.uid) {
        this.clearPlaybackStateIfAccountChanged(userData.userInfo.uid)
        syncRewardStorageForAccount(userData.userInfo.uid)
        this.userInfo = userData.userInfo
        this.uid = userData.userInfo.uid
        localStorage.setItem('uid', userData.userInfo.uid)
        localStorage.setItem('user_info', JSON.stringify(userData.userInfo))
        refreshAnalyticsUserProperties()
      }
    },
    async handleThirdPartyLogin(loginFrom, oauthToken) {
      try {
        // loginFrom: 2 代表 Google
        const response = await loginWithThirdParty(loginFrom, oauthToken, this.deviceId)
        if (response && response.userInfo) {
          const userData = response.userInfo
          this.clearPlaybackStateIfAccountChanged(userData.uid)
          syncRewardStorageForAccount(userData.uid)
          // 更新 state
          this.uid = userData.uid
          this.userInfo = userData
          // 持久化存储
          localStorage.setItem('uid', userData.uid)
          localStorage.setItem('user_info', JSON.stringify(userData))
          refreshAnalyticsUserProperties()
          return true
        }
      } catch (error) {
        console.error('Login Failed:', error)
        return false
      }
    },
    async updateUserInfo() {
      try {
        return await this.fetchUserDetail()
      } catch (error) {
        console.error('updateUserInfo failed:', error)
      }
    },
    /** Cognito 登录后拉取 /api/user/detail（请求拦截器会附带 Authorization: idToken） */
    async fetchUserDetail() {
      this.deviceId = await ensureDeviceId()
      const userData = await getUserDetail()
      if (userData?.userInfo?.uid) {
        logger.debug('fetchUserDetail userInfo = ', userData.userInfo)
        this.clearPlaybackStateIfAccountChanged(userData.userInfo.uid)
        syncRewardStorageForAccount(userData.userInfo.uid)
        this.userInfo = userData.userInfo
        this.uid = userData.userInfo.uid
        localStorage.setItem('uid', userData.userInfo.uid)
        localStorage.setItem('user_info', JSON.stringify(userData.userInfo))
        refreshAnalyticsUserProperties()
      }
      return userData
    },
    applyCognitoSession(session) {
      const idToken = session?.idToken || ''
      const accessToken = session?.accessToken || ''
      const refreshToken = session?.refreshToken || ''
      const profileFromSession = session?.profile || {}
      const idPayload = decodeJwtPayload(idToken) || {}
      const profile = { ...idPayload, ...profileFromSession }

      const picture = pickAvatarFromProfile(profile)
      const prevSub = this.cognitoProfile?.sub || ''
      if (prevSub && profile.sub && String(prevSub) !== String(profile.sub)) {
        this.clearPlaybackState()
      }

      logger.debug('[Cognito] applyCognitoSession — profile from session:', profileFromSession)
      logger.debug('[Cognito] applyCognitoSession — merged profile (incl. idToken):', {
        sub: profile.sub,
        email: profile.email,
        name: profile.name,
        picture
      })

      this.cognitoTokens = {
        idToken,
        accessToken,
        refreshToken,
        tokenType: 'Bearer',
        expiresAt: Date.now() + 3600 * 1000
      }
      this._sessionReady = null
      this.cognitoProfile = {
        sub: profile.sub || '',
        email: profile.email || '',
        name: profile.name || profile.email || '',
        picture
      }
      syncRewardStorageForAccount(this.cognitoProfile.sub)
      this.userInfo = {
        ...this.userInfo,
        loginFrom: this.userInfo?.loginFrom || 2,
        nickname: this.cognitoProfile.name || this.userInfo?.nickname || 'Guest',
        avatar: picture || this.userInfo?.avatar || ''
      }
      localStorage.setItem('cognito_tokens', JSON.stringify(this.cognitoTokens))
      localStorage.setItem('cognito_profile', JSON.stringify(this.cognitoProfile))
      localStorage.setItem('user_info', JSON.stringify(this.userInfo))
    },
    clearCognitoSession({ clearRewardBinding = true } = {}) {
      this.cognitoTokens = null
      this.cognitoProfile = null
      localStorage.removeItem('cognito_tokens')
      localStorage.removeItem('cognito_profile')
      if (clearRewardBinding) {
        clearRewardBoundSub()
      }
    },
    async signOut() {
      this.clearCognitoSession({ clearRewardBinding: false })
      this._sessionReady = null
      clearRewardTasksCache()

      try {
        const userData = await this.fetchUserDetail()
        clearRewardTasksCache()
        return userData
      } catch (error) {
        console.error('fetch guest user detail after sign out failed:', error)
        this.clearPlaybackState()
        clearRewardBoundSub()
        this.uid = ''
        this.userInfo = {
          ...this.userInfo,
          uid: '',
          uId: '',
          loginFrom: 0,
          nickname: 'Guest',
          avatar: '',
          email: null,
          vip: false,
          vipExpireAt: null,
          watchCoins: 0,
          watchBonus: 0
        }
        localStorage.removeItem('uid')
        localStorage.setItem('user_info', JSON.stringify(this.userInfo))
        refreshAnalyticsUserProperties()
      }
    }
  }
})
